import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react"

const PlayerContext = createContext()
const STORAGE_KEY = "mixtape-player-state"

const defaultState = {
  showPlayer: false,
  isPlaying: false,
  currentTime: 0,
}

const getInitialState = () => {
  if (typeof window === "undefined") return defaultState

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? { ...defaultState, ...JSON.parse(stored) } : defaultState
  } catch (e) {
    console.error("Failed to load mixtape state:", e)
    return defaultState
  }
}

export const PlayerProvider = ({ children }) => {
  const [state, setState] = useState(getInitialState)

  // Persist state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch (e) {
      console.error("Failed to persist mixtape state:", e)
    }
  }, [state])

  // Handle page visibility changes
  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) {
        try {
          const stored = localStorage.getItem(STORAGE_KEY)
          if (stored) {
            const parsed = JSON.parse(stored)
            if (
              parsed.timestamp &&
              Date.now() - parsed.timestamp < 5 * 60 * 1000
            ) {
              const { timestamp, ...restored } = parsed
              setState((prev) => ({ ...prev, ...restored }))
            }
          }
        } catch (e) {
          console.error("Error restoring player state:", e)
        }
      } else {
        try {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ ...state, timestamp: Date.now() })
          )
        } catch (e) {
          console.error("Error saving player state:", e)
        }
      }
    }

    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [state])

  // --- CONTROL FUNCTIONS ---
  const setShowPlayer = useCallback((show) => {
    setState((prev) => ({ ...prev, showPlayer: show }))
  }, [])

  useEffect(() => {
    const handleStartPlayer = () => setShowPlayer(true)
    window.addEventListener("startPersistentPlayer", handleStartPlayer)
    return () =>
      window.removeEventListener("startPersistentPlayer", handleStartPlayer)
  }, [setShowPlayer])

  const setIsPlaying = useCallback((isPlaying) => {
    setState((prev) => ({ ...prev, isPlaying }))
  }, [])

  const setCurrentTime = useCallback((time) => {
    setState((prev) => ({ ...prev, currentTime: time }))
  }, [])

  const togglePlaying = useCallback(() => {
    setState((prev) => ({ ...prev, isPlaying: !prev.isPlaying }))
  }, [])

  const togglePlayerVisibility = useCallback(() => {
    setState((prev) => ({ ...prev, showPlayer: !prev.showPlayer }))
  }, [])

  const value = {
    ...state,
    setShowPlayer,
    setIsPlaying,
    setCurrentTime,
    togglePlaying,
    togglePlayerVisibility,
  }

  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  )
}

export const usePlayer = () => {
  const ctx = useContext(PlayerContext)
  if (!ctx) {
    throw new Error("usePlayer must be used within a PlayerProvider")
  }
  return ctx
}
